/**
 * Typed mirror of the Flex Transactions *write* API (PVAL-26291):
 * `POST /flex-transactions/actions`.
 *
 * Both the verb list and the id sides are deliberately open-ended. `match` / `unmatch` are what
 * PVAL-26291 shipped; `hold` / `ignore` of external transactions (PVAL-26309 here, helios !498 /
 * PVAL-26500 server-side) ride the same endpoint and act on the Prometheus side ALONE — so a verb
 * must not be forced to carry both id sets.
 *
 * `helios-session-id` is required by the endpoint but is attached globally by the app's
 * SessionInterceptor, so no call site sets it (same note as `transactions-query.model.ts`).
 */

/**
 * Action verbs the endpoint accepts. An enum, not a closed union, so a new verb is one member here.
 * Values must stay lowercase — the server's `FlexTransactionActionType` is lowercase `match` /
 * `unmatch` (PVAL-26291 CHANGE 2) and lowercase `hold` / `ignore` (helios !498). Same shape as the
 * 1.0 panels' `ExternalActionType`.
 *
 * `hold` and `ignore` are Prometheus-only and have NO inverse here: !498 ships no `unhold` /
 * `unignore` flex verb, and the state they write is visible and reversible only from the
 * Transactions 1.0 External Transactions panel until the flex read path carries it.
 */
export enum FlexTransactionActionType {
  match = 'match',
  unmatch = 'unmatch',
  hold = 'hold',
  ignore = 'ignore',
}

/**
 * Wire shape. Both id sets are OPTIONAL: the server declares them `Set<String> = emptySet()`, so an
 * absent key deserializes to an empty set, and a verb that acts on one side only must not be forced
 * to send a meaningless empty array for the other. Omit a side the verb does not use rather than
 * sending `[]` — which is exactly the flaw the 1.0 factories
 * (`createExternalTransactionsActionRequest`, `createTransactionAction`) have, and the reason
 * hold / unhold / ignore / unignore there ship a pointless `cwTranRowIds: []` on every call.
 */
export interface FlexTransactionActionRequest {
  actionType: FlexTransactionActionType
  /** flextransactions.transactions.transaction_id — Enfusion (Base grid) row ids. */
  enfnTranIds?: string[]
  /** flextransactions.external_transactions.ext_tran_id — Prometheus (Comparison grid) row ids. */
  extTranIds?: string[]
}

/** One command the action created. `command` is the full perseus command; the panel does not read
 * it, so it stays opaque rather than mirroring a shape we would have to keep in step for nothing. */
export interface CommandCreationResponse {
  commandId: string
  command?: unknown
  /** True when the command needs approval before it will apply — the commands drawer owns that. */
  needsReview?: boolean
}

/**
 * The 200 body: a wrapper object, NOT a bare array of command ids.
 *
 * The wrapper is deliberate on the server's side — a top-level array cannot gain a field without
 * breaking clients — so read ids as `res.commands.map(c => c.commandId)` rather than indexing the
 * response itself.
 *
 * There is NO soft-validation arm here (PVAL-26130 Dev Open Questions #3, now answered): unlike
 * `clearwater-transactions-action.model.ts`, flex does not return `200` + `{ validationId, message }`.
 * Every rejection is a real 400 whose body is a {@link FlexActionValidation}`[]`.
 */
export interface FlexTransactionActionResponse {
  commands: CommandCreationResponse[]
}

/** The ids of the commands an action created, in response order. */
export const createdCommandIds = (
  response: FlexTransactionActionResponse | null | undefined,
): string[] =>
  (response?.commands ?? [])
    .map(command => command.commandId)
    .filter((id): id is string => !!id)

/**
 * One entry of a 400 body. The endpoint answers every rejection with a JSON array of these.
 *
 * `validationId` is a stable code and is what user-facing copy must be keyed on;
 * `message` is prose, and is NOT reliably present — `ReactiveGlobalExceptionMapper` nulls the
 * message outside lower environments, which is precisely why the code is carried in the body too.
 * Rendering the message alone would show a blank in production.
 */
export interface FlexActionValidation {
  validationId: string
  message?: string
}

/**
 * Every validation code the endpoint can answer with, mapped to the copy shown in the panel.
 *
 * Every one of these is reachable. The UI does not pre-empt any of the server's validation rules —
 * `availableActions` is the only thing that disables a menu item — so a rule the user breaks comes
 * back as a 400 and is named here rather than shown as a generic failure.
 */
export const FLEX_ACTION_VALIDATION_COPY: Record<string, string> = {
  bothSidesRequired:
    'A match needs at least one Enfusion and one Prometheus transaction.',
  idsRequired: 'Select at least one transaction to action.',
  rowsNotFound:
    'One or more selected transactions are no longer available. Refresh and try again.',
  accountNotInSession:
    'One or more selected transactions belong to an account that is not in this session.',
  crossAccountMatch: 'A match must be within a single account.',
  missingDataSourceType:
    'A selected Prometheus transaction has no data source and cannot be matched.',
  unknownDataSourceType:
    'A selected Prometheus transaction has an unrecognised data source and cannot be matched.',
  promSourcesSpan:
    'A single match cannot combine Custody and Trading transactions.',
  alreadyMatched: 'These transactions are already matched to each other.',
  cannotMergeGroups:
    'These transactions are in different match groups. Merging existing groups is not supported — unmatch one first.',
  sideSpansMultipleGroups:
    'One side of this selection spans more than one match group. Unmatch them separately.',
  notMatched:
    'These transactions are not matched, so there is nothing to undo.',
  incoherentMembers:
    'This combination of transactions cannot form a valid match group.',
  // The three codes helios !498 adds for `hold` / `ignore`. Keyed on the camelCase WIRE values —
  // the Kotlin constants are SCREAMING_SNAKE, the serialised values are not.
  invalidActionForStatus:
    'One or more selected transactions are already held or ignored, or their current status does not allow this action. Hold and Ignore are shown and reversed in the Transactions 1.0 External Transactions panel.',
  invalidExtTranId:
    'One or more selected Prometheus transactions are no longer recognised. Refresh and try again.',
  // Unreachable from this panel — `ACTION_SIDES` marks the Prometheus side `required`, so an empty
  // scope throws client-side before the POST. Mapped anyway because it is one line.
  extTranIdsRequired:
    'Select at least one Prometheus transaction to hold or ignore.',
}

/**
 * Disclosure shown after an action the server ACCEPTED, per verb.
 *
 * Hold and Ignore are one-way doors from this panel and the user has to be told so on the path
 * where it matters — the successful one. Three facts make it a disclosure rather than a
 * confirmation: !498 writes a durable `ExtTranStatus`, the flex read projection does not carry it
 * (`FlexExternalTransactionRow.matchStatus` is hardcoded `"UNMATCHED"`), and the contract has no
 * `unhold` / `unignore` verb. So the optimistic status is the only evidence the write happened, it
 * is gone at the next fetch, and the way back is another panel.
 *
 * Putting this only in `FLEX_ACTION_VALIDATION_COPY.invalidActionForStatus` would tell it to the
 * user whose action was REJECTED and leave the one whose action worked to discover the disappearing
 * status for themselves. Match / unmatch carry no note: both are readable and reversible here, and
 * the reconcile refetch is what reports them.
 */
export const FLEX_ACTION_SUCCESS_NOTE: Partial<
  Record<FlexTransactionActionType, string>
> = {
  [FlexTransactionActionType.hold]:
    'Hold submitted. Transactions 2.0 cannot read this status back yet, so it disappears from this grid at the next refresh, and there is no Unhold here — view or release a hold in the Transactions 1.0 External Transactions panel.',
  [FlexTransactionActionType.ignore]:
    'Ignore submitted. Transactions 2.0 cannot read this status back yet, so it disappears from this grid at the next refresh, and Unignore here does not reverse it — view or reverse an ignore in the Transactions 1.0 External Transactions panel.',
}

/** Whether a verb must carry an id side, may carry it, or must never carry it. */
export type ActionSide = 'required' | 'optional' | 'unused'

/** The two id sides of one verb. Keys match the request fields they govern. */
export interface ActionSides {
  enfn: ActionSide
  ext: ActionSide
}

/**
 * Which id sides each verb uses. A `Record` over the enum is the point: adding a verb without
 * declaring its sides is a compile error, so "which sides does this action need?" is answered once,
 * in one place, instead of being re-derived at every call site. Read by the menu's enable checks
 * and by {@link buildFlexTransactionActionRequest}. Matches how this panel already encodes its
 * rules — `MATCH_TARGETS`, `ROLE_ALLOWED`, `MATCH_FIELD_TARGETS`.
 */
export const ACTION_SIDES: Record<FlexTransactionActionType, ActionSides> = {
  // A match group spans >= 2 sources, so a match needs at least one row from each grid.
  [FlexTransactionActionType.match]: { enfn: 'required', ext: 'required' },
  // Unmatch carries only the right-clicked grid's rows — the server resolves them to match groups
  // and dissolves each one — so either side alone is valid and neither is 'required'.
  [FlexTransactionActionType.unmatch]: { enfn: 'optional', ext: 'optional' },
  // Hold and Ignore act on External (Prometheus) transactions ONLY: !498 resolves them through
  // `resolveExtRowsForHoldOrIgnore` and emits `HOLD_EXT_TRAN` / `IGNORE_EXT_TRAN` against
  // `(extTranId, accountId)`. Marking the Enfusion side `unused` is what omits `enfnTranIds` from
  // the body rather than sending `[]`, and makes passing Enfusion ids for these verbs throw; the
  // Prometheus side being `required` is what makes an all-synthetic scope fail before the POST
  // instead of silently posting nothing.
  [FlexTransactionActionType.hold]: { enfn: 'unused', ext: 'required' },
  [FlexTransactionActionType.ignore]: { enfn: 'unused', ext: 'required' },
}

/** Candidate id sets, before the side table has been applied. */
export interface ActionIdSets {
  enfnTranIds?: string[]
  extTranIds?: string[]
}

type SideKey = keyof ActionSides

/** Side -> the request field it populates, and a label for assertion messages. */
const SIDE_FIELD: Record<SideKey, 'enfnTranIds' | 'extTranIds'> = {
  enfn: 'enfnTranIds',
  ext: 'extTranIds',
}

const SIDE_LABEL: Record<SideKey, string> = {
  enfn: 'Enfusion',
  ext: 'Prometheus',
}

const SIDE_ORDER: readonly SideKey[] = ['enfn', 'ext']

const distinct = (ids: string[] | undefined): string[] => [
  ...new Set(ids ?? []),
]

/**
 * Copy for the "a required side carried no ids" abort, keyed by verb then side.
 *
 * The generic message below it names the verb and the side, which is right for `match`: that abort
 * is a caller bug — the menu pre-flights both sides — and the text is read by whoever is debugging
 * it. For `hold` / `ignore` the SAME abort is reachable by a user: a scope whose rows all carry the
 * adapter's synthetic `rowId` loses every id in `mapRowsToActionIds`, leaving the required
 * Prometheus side empty. `submitAction` renders `err.message` verbatim in-panel, so these two carry
 * copy written for the person who right-clicked, saying what cannot be done and what to try — not
 * `hold requires at least one Prometheus transaction id`.
 */
const REQUIRED_SIDE_MISSING_COPY: Partial<
  Record<FlexTransactionActionType, Partial<Record<SideKey, string>>>
> = {
  [FlexTransactionActionType.hold]: {
    ext: 'These transactions cannot be held: none of them came back with a Prometheus transaction id. Refresh the grid and try again.',
  },
  [FlexTransactionActionType.ignore]: {
    ext: 'These transactions cannot be ignored: none of them came back with a Prometheus transaction id. Refresh the grid and try again.',
  },
}

/**
 * Build the request body for a verb, reading {@link ACTION_SIDES} rather than hardcoding which
 * arrays a verb needs.
 *
 *  - an `unused` side is omitted, and passing ids for one is a programming error;
 *  - a `required` side must be non-empty;
 *  - an `optional` side is omitted when empty rather than sent as `[]`;
 *  - and the one rule the table cannot express — an action must carry at least one id somewhere —
 *    is asserted here. That is what stops an `unmatch` with neither side from being posted. A union
 *    arm per side-combination would buy little and grow badly as verbs are added, so the assertion
 *    lives in the builder, not in the type.
 *
 * Throws before any HTTP call, so a caller that skipped its pre-flight checks fails loudly in
 * development rather than producing a 400. The thrown message is rendered in-panel verbatim, which
 * is why {@link REQUIRED_SIDE_MISSING_COPY} overrides it for the verbs whose empty-side abort a
 * user can actually reach.
 *
 * @param actionType the verb being submitted
 * @param ids candidate id sets, by side
 * @returns the request body, with unused / empty-optional sides absent
 */
export function buildFlexTransactionActionRequest(
  actionType: FlexTransactionActionType,
  ids: ActionIdSets,
): FlexTransactionActionRequest {
  const sides = ACTION_SIDES[actionType]
  const request: FlexTransactionActionRequest = { actionType }
  let carried = 0

  SIDE_ORDER.forEach(side => {
    const rule = sides[side]
    const values = distinct(ids[SIDE_FIELD[side]])

    if (rule === 'unused') {
      if (values.length) {
        throw new Error(
          `${actionType} does not act on the ${SIDE_LABEL[side]} side`,
        )
      }
      return
    }

    if (!values.length) {
      if (rule === 'required') {
        throw new Error(
          REQUIRED_SIDE_MISSING_COPY[actionType]?.[side] ??
            `${actionType} requires at least one ${SIDE_LABEL[side]} transaction id`,
        )
      }
      return
    }

    request[SIDE_FIELD[side]] = values
    carried += values.length
  })

  if (!carried) {
    throw new Error(`${actionType} requires at least one transaction id`)
  }

  return request
}

/**
 * Every validation code `POST transactions/save` (helios !518) answers a 400 with, mapped to the copy
 * the Create/Edit/Post dialogs show (PVAL-26440). Keyed on the stable `validationId`, never the
 * message, for the reason {@link FlexActionValidation} gives: the prose is nulled in production.
 */
export const TRANSACTION_SAVE_VALIDATION_COPY: Record<string, string> = {
  transactionsRequired:
    'There is nothing to save. Change a value and try again.',
  accountIdRequired:
    'This transaction has no account, so it cannot be saved. Open it from an account and try again.',
  transactionRequired: 'One of the transactions being saved was empty.',
  transactionNotObject:
    'One of the transactions being saved was not in a form the server accepts.',
  transactionIdInvalid:
    'This transaction’s id was not recognised. Refresh the grid and try again.',
  accountNotInSession:
    'This transaction belongs to an account that is not in this session.',
  categoryRequiredForCreate:
    'A new transaction must name its transaction type. Reopen the dialog from the New menu and try again.',
}

/** The copy for every save outcome that is not a validation code (PVAL-26440). */
export const TRANSACTION_SAVE_COPY = {
  /** 409 — the record changed underneath the dialog. Retrying is safe. */
  conflict:
    'This transaction was changed concurrently by someone else. Try again.',
  /** 403 — the user may not save this. Rendered if the server ever sends one (PVAL-26793). */
  forbidden: 'You do not have permission to save this transaction.',
  /** 5xx, a dropped connection, or a 200 the client could not read. */
  generic: 'The save did not complete.',
  /**
   * Appended to {@link generic}: a failure after the request left the browser may still have
   * created the command, and a blind retry would create a second one.
   */
  checkDrawer: 'Check the commands drawer before retrying.',
  /** No account to save under — refused before any request is made. */
  noAccount:
    'This transaction has no account to save under, so nothing was sent.',
  /** An edit opened on a row carrying neither a gLEntryId nor a tradeId. */
  noRecordId:
    'This transaction has no trade or GL entry id to save under, so nothing was sent.',
} as const
